import { Badge, Box, Divider, Grid2, Grow, Typography } from "@mui/material";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Tooltip, { TooltipProps, tooltipClasses } from "@mui/material/Tooltip";
import { styled } from "@mui/material/styles";
import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAppSelector } from "store/store";
import { UserRoleTypes } from "utils/common";
import { refetchQuery } from "utils/helpers";
import { QueryKeys } from "utils/queryKey";
import { LockIcon, SolarSettingsOutlineIcon } from "./Icons";

const LightTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(() => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: "#DDEAEE",
    color: "var(--primary-900)",
    fontSize: 11,
  },
  [`& .${tooltipClasses.arrow}`]: {
    "&:before": {
      backgroundColor: "#DDEAEE",
    },
  },
}));

const DesignedDrawer = styled("div")((props: any) => {
  return {
    flexShrink: 0,
    borderRight: `solid 1px`,
    borderColor: `var(--neutral-100)`,
    whiteSpace: "nowrap",
    boxSizing: "border-box",
    width: props["data-isopen"] ? 241 : 65,
    zIndex: 5,
    transition: "all 0.3s",
    // padding: 8,
    maxHeight: "calc(100vh - 66px)",
  };
});

interface Props {
  menuList: {
    name: string;
    icon: any;
    show: any;
    to: string;
    role: string[];
    canAccess?: boolean;
    accountType?: any;
    iconSize?: number;
  }[];
}

const SideMenu = ({ menuList }: Props) => {
  const sidebarOpen = useAppSelector((state) => state.app.sidebarOpen);
  const userInfo = useAppSelector((state) => state?.user?.userData);

  const navigate = useNavigate();

  const { pathname } = useLocation();

  const [hoverIndex, setHoverIndex] = useState(null);

  return (
    <DesignedDrawer
      onMouseLeave={() => setHoverIndex(null)}
      data-isopen={sidebarOpen}
    >
      <List
        sx={{
          padding: 1,
          overflow: "hidden",
          transition: "overflow 0.3s",
          minHeight:
            userInfo?.userRole === UserRoleTypes.advisor
              ? "calc(100vh - 194px)"
              : "calc(100vh - 130px)",
          maxHeight:
            userInfo?.userRole === UserRoleTypes.advisor
              ? "calc(100vh - 194px)"
              : "calc(100vh - 130px)",
          "&:hover": {
            overflow: "hidden  auto",
            transition: "overflow 0.3s",
          },
        }}
      >
        {menuList
          .filter(
            (dataItem) =>
              dataItem?.role?.includes(userInfo?.userRole) &&
              dataItem?.accountType?.includes(userInfo?.accountType),
          )
          .filter((dataItem) =>
            dataItem?.accountType?.includes(userInfo?.accountType),
          )
          ?.filter((showData) => showData?.show)
          .map((text, index) => (
            <span key={index}>
              <LightTooltip
                key={index}
                open={index === hoverIndex && !sidebarOpen}
                title={text.name}
                placement={"right"}
                arrow
                disableHoverListener={sidebarOpen}
              >
                <ListItem
                  onMouseEnter={() => setHoverIndex(index)}
                  onClick={() => {
                    if (text.canAccess) {
                      navigate(text.to);
                    }
                  }}
                  key={text.name}
                  disablePadding
                  sx={{
                    display: "block",
                    transition: "all 0.3s",
                    "&:hover": {
                      transition: "all 0.3s",
                    },
                  }}
                >
                  <ListItemButton
                    disabled={!text.canAccess}
                    sx={{
                      minHeight: 48,
                      justifyContent: sidebarOpen ? "initial" : "center",
                      px: 2.5,
                      fontWeight: 600,
                      borderRadius: "10px",
                      background: pathname.includes(text.to)
                        ? `var(--primary-50)`
                        : `var(--white)`,
                      color: pathname.includes(text.to)
                        ? `var(--primary-900)`
                        : "var(--neutral-500)",
                      "&:hover": {
                        background: pathname.includes(text.to)
                          ? `var(--primary-50)`
                          : "var(--neutral-50)",
                      },
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        minWidth: 0,
                        mr: sidebarOpen ? 2 : "auto",
                        justifyContent: "center",
                        color: pathname.includes(text.to)
                          ? `var(--primary-900)`
                          : "var(--neutral-500)",
                      }}
                    >
                      {!sidebarOpen && !text?.canAccess ? (
                        <Badge
                          badgeContent={<LockIcon width={16} height={16} />}
                          color="primary"
                        >
                          <text.icon
                            width={text?.iconSize || 20}
                            height={text?.iconSize || 20}
                          />
                        </Badge>
                      ) : (
                        <text.icon
                          width={text?.iconSize || 20}
                          height={text?.iconSize || 20}
                        />
                      )}
                    </ListItemIcon>
                    <ListItemText
                      primary={text.name}
                      sx={{
                        opacity: sidebarOpen ? 1 : 0,
                        "& .MuiTypography-root": {
                          fontWeight: 600,
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          maxWidth: "150px",
                        },
                      }}
                    />
                    {!text?.canAccess && <LockIcon width={16} height={16} />}
                  </ListItemButton>
                </ListItem>
              </LightTooltip>
            </span>
          ))}
      </List>
      <Box sx={{ marginTop: "auto" }}>
        <Grid2 container>
          {userInfo?.userRole === UserRoleTypes.advisor && (
            <Grid2 size={12}>
              <Divider />
            </Grid2>
          )}
          {userInfo?.userRole === UserRoleTypes.advisor && (
            <Grid2 size={12} mt={1} px={1}>
              <ListItemButton
                onClick={() => {
                  navigate(`/app/company-onboarding`);
                  refetchQuery(QueryKeys.GET_USER_DETAIL);
                }}
                sx={{
                  minHeight: 48,
                  justifyContent: sidebarOpen ? "initial" : "center",
                  fontWeight: 600,
                  borderRadius: "10px",
                  background: pathname.includes("/app/company-onboarding")
                    ? `var(--primary-50)`
                    : `var(--white)`,
                  color: pathname.includes("/app/company-onboarding")
                    ? `var(--primary-900)`
                    : "var(--neutral-500)",
                  "&:hover": {
                    background: pathname.includes("/app/company-onboarding")
                      ? `var(--primary-50)`
                      : "var(--neutral-50)",
                  },
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 0,
                    mr: sidebarOpen ? 2 : "auto",
                    justifyContent: "center",
                    color: pathname.includes("/app/company-onboarding")
                      ? `var(--primary-900)`
                      : "var(--neutral-500)",
                  }}
                >
                  <SolarSettingsOutlineIcon width={20} height={20} />
                </ListItemIcon>
                <ListItemText
                  primary={"Get Started"}
                  sx={{
                    display: sidebarOpen ? "block" : "none",
                    "& .MuiTypography-root": {
                      fontWeight: 600,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      maxWidth: "150px",
                    },
                  }}
                />
              </ListItemButton>
            </Grid2>
          )}
          <Grid2 size={12} mt={1}>
            <Divider />
          </Grid2>
          <Grid2
            size={12}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              p: 1,
              marginTop: "auto",
            }}
          >
            <Grow timeout={300} in={sidebarOpen}>
              <Typography
                sx={{ display: sidebarOpen ? "flex" : "none" }}
                fontSize={12}
              >
                Powered By
              </Typography>
            </Grow>
            <Grow timeout={300} in={sidebarOpen}>
              <img
                onError={({ currentTarget }) => {
                  currentTarget.src = "/Logos/errorImg.svg";
                }}
                alt=""
                src={"/Logos/iconFullLogo.png"}
                width={"50%"}
                style={{ display: sidebarOpen ? "flex" : "none" }}
                draggable="false"
              />
            </Grow>
            <Grow timeout={300} in={!sidebarOpen}>
              <img
                onError={({ currentTarget }) => {
                  currentTarget.src = "/Logos/errorImg.svg";
                }}
                alt=""
                src={"/Logos/iconLogo.png"}
                width={"30px"}
                style={{ display: !sidebarOpen ? "flex" : "none" }}
                draggable="false"
              />
            </Grow>
          </Grid2>
        </Grid2>
      </Box>
    </DesignedDrawer>
  );
};
export default SideMenu;
